













































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import Icon from "@/components/Icon.vue";
import Spinner from "@/components/Spinner.vue";
import DropDownSimple from "@/components/dropdown/DropDownSimple.vue";
import { SharedState } from "@/common/state";
import { state as ds_state } from "@/views/customize/data-sources/data/state";
import moment from "moment";
import { pdf_state } from "@/views/reports/data/pdf/state.pdf";
import { state as reports_state } from "@/views/reports/data/state";
import {
  AggregationTimeFrameOption,
  allowedAggregationTypes,
  buildManagementDeckSections,
  getAggregationTimeFrameOptions,
} from "@/views/reports/data/pdf/aggregation-timeframes";

/** what a deck sends: one section, as buildManagementDeckSections takes it */
type DeckSection = Parameters<typeof buildManagementDeckSections>[0][number];

/**
 * One management deck, as an Executive Report.
 *
 * These decks used to sit in the Reports tab's template picker, which is built
 * for composing sections and applying an aggregation across them. A deck has
 * neither — it renders a whole report from a single section — and running it
 * through that flow is what produced the repeated pages and the aggregation
 * options the deck could not honour.
 *
 * Here there is one control, the period, which is all a deck actually takes.
 * The section payload comes from buildManagementDeckSections so this and the
 * regenerate flow cannot diverge.
 */
/**
 * How many periods the picker offers. Nobody generates a management report for
 * three years ago, and an unbounded list makes the panel taller than the
 * viewport — at which point it flips upward and is clipped by the page
 * container, which has `overflow-hidden`.
 */
const PERIOD_LIMIT = 12;

/** either a plain calendar period or an aggregated one, depending on the deck */
type PeriodOption = { id: string; name: string } & Record<string, any>;

export default Vue.extend({
  components: { Icon, Spinner, DropDownSimple },
  props: {
    deck: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const is_generating = ref(false);

    const question_id = (props.deck as any).question_id as string;

    /** a template this user saved with the deck in it, if there is one */
    const template = computed(() =>
      pdf_state.report_templates.items.value.find((t) =>
        (t.sections || []).some((s) => s.id === question_id)
      )
    );

    /**
     * The deck's section. It used to come from a saved template alone, and
     * `/templates/user` returns only the templates a user wrote plus the org's
     * public ones (a biz user gets only their own). So the card worked for
     * whoever had saved a deck template and read "not available" for everyone
     * else in the same org.
     *
     * The report service lists both decks in its question catalog, the same
     * for every org, so that is the source. A template the user saved still
     * wins, so their reports keep pointing at it. The last fallback mirrors
     * the catalog entry, for when the catalog did not load (its loader returns
     * an empty list if the section images fail).
     */
    const section = computed<DeckSection>(() => {
      const saved = template.value
        ? template.value.sections.find((s) => s.id === question_id)
        : undefined;
      if (saved) return saved;
      const listed = pdf_state.template_questions.value.find(
        (q) => q.id === question_id
      );
      if (listed) return listed;
      return {
        id: question_id,
        question: (props.deck as any).name,
        data_source: "motion_365",
        parameters: [],
      };
    });

    /**
     * Which aggregation shapes this deck accepts. The quarterly deck answers
     * ["quarter"]; the monthly one answers "none" — it takes a plain calendar
     * month, not an aggregation. Offering it aggregation periods, or sending
     * one, is precisely the mismatch that made these decks misbehave in the
     * template flow, so the two cases are kept apart here.
     */
    const allowed = computed(() => allowedAggregationTypes([question_id]));

    const is_aggregated_deck = computed(() => allowed.value !== "none");

    const periods = computed<PeriodOption[]>(() => {
      if (!is_aggregated_deck.value) {
        /*
         * Months only. The shared list leads with "Current week" and "Last
         * week", which a *monthly* report cannot be generated for, and it runs
         * back to the org's start date — so the panel grew long enough to flip
         * upward and be clipped by the page container. Filtering to months and
         * capping the window fixes the meaning and the overflow together.
         */
        return ds_state.ext
          .get_in_motion_source_time_frames("motion_365")
          .filter((d: any) => d.type === "Month")
          .slice(0, PERIOD_LIMIT)
          .map((d: any) => ({ ...d, id: d.displayName, name: d.displayName }));
      }
      // same source the regenerate flow uses, with the same two-year fallback
      const from = SharedState.basedOn.value && SharedState.basedOn.value.dateFrom;
      const org_start =
        from && typeof from === "string"
          ? from
          : moment().subtract(2, "years").format("YYYY-MM-DD");
      return getAggregationTimeFrameOptions(
        org_start,
        allowed.value as AggregationTimeFrameOption["aggregation_type"][]
      )
        .slice(0, PERIOD_LIMIT)
        .map((d) => ({ ...d, id: d.id, name: d.name })) as PeriodOption[];
    });

    /**
     * Unavailable only when there is no period to report on: an org with no
     * Microsoft 365 months yet, which is what "not available for your
     * organization yet" means.
     */
    const can_generate = computed(() => periods.value.length > 0);

    const selected_period = ref<PeriodOption | null>(null);
    const set_selected_period = (o: PeriodOption) => {
      selected_period.value = o;
    };
    // default to the most recent period the deck offers
    const ensure_selection = () => {
      if (!selected_period.value && periods.value.length) {
        selected_period.value = periods.value[0];
      }
    };

    const scope_note = computed(() => {
      ensure_selection();
      return (props.deck as any).period === "quarter"
        ? "Covers a full calendar quarter."
        : "Covers a single calendar month.";
    });

    const generate = () => {
      const t = template.value;
      const sel = selected_period.value;
      if (!sel || is_generating.value) {
        return;
      }
      is_generating.value = true;
      try {
        // a deck is self-contained; only its own section is sent, never the
        // summary-plus-one-per-month expansion
        const to_send = [section.value];
        const sections = is_aggregated_deck.value
          ? buildManagementDeckSections(
              to_send,
              sel as AggregationTimeFrameOption,
              sel.name
            )
          : to_send.map((s) => ({
              data_source: s.data_source,
              id: s.id,
              question: s.question,
              application: s.application,
              parameters: s.parameters,
              parameters_values: s.parameters_values,
              time: {
                type: "timeframe" as const,
                time_type: (sel as any).type,
                time_value: (sel as any).from,
                displayName: (sel as any).displayName || sel.name,
              },
            }));
        pdf_state.generate_report_from_template({
          name: `${(props.deck as any).name} — ${sel.name}`,
          sections: sections as any,
          // The service stores this and never loads it for an on-demand
          // report (only schedules use it), so without a saved template the
          // deck's own id names the report's origin.
          template_id: t ? t.id : question_id,
          // the deck renders into the Reports tab; say so, and offer the trip
          toast_action: {
            name: "View in Reports",
            onClick: () =>
              reports_state.set_current_tab({
                id: "pdf",
                name: "Reports",
                icon: "pdf",
              }),
          },
        });
      } finally {
        is_generating.value = false;
      }
    };

    return {
      is_generating,
      can_generate,
      periods,
      selected_period,
      set_selected_period,
      scope_note,
      generate,
    };
  },
});
